// client/src/flow/components/NavigationHandler.js
// Componente wrapper para processar links de navegação com WhatsApp inteligente

import React, { useMemo } from 'react';
import { convertWhatsAppUrl } from '../../utils/whatsappUtils';

/**
 * Hook para processar itens de navegação e converter URLs do WhatsApp
 */
export const useProcessedNavigationItems = (navigationItems) => {
  return useMemo(() => {
    if (!navigationItems || !Array.isArray(navigationItems)) {
      return navigationItems;
    }

    return navigationItems.map(item => {
      // Se o item tem contactInfo com WhatsApp, converte
      if (item.contactInfo?.whatsapp) {
        return {
          ...item,
          contactInfo: {
            ...item.contactInfo,
            whatsapp: convertWhatsAppUrl(item.contactInfo.whatsapp)
          }
        };
      }

      // Se o item tem whatsappUrl diretamente, converte
      if (item.whatsappUrl) {
        return {
          ...item,
          whatsappUrl: convertWhatsAppUrl(item.whatsappUrl)
        };
      }

      return item;
    });
  }, [navigationItems]);
};

/**
 * Componente de botão de navegação com suporte a WhatsApp inteligente
 */
export const NavigationButton = ({ 
  item, 
  onClick, 
  className = '', 
  children 
}) => {
  const handleClick = () => {
    if (item.type === 'contact' && item.contactInfo?.whatsapp) {
      const url = convertWhatsAppUrl(item.contactInfo.whatsapp);
      window.open(url, '_blank');
    } else if (item.type === 'link' && item.url) {
      // Se for um link do WhatsApp, converte também
      const url = item.url.includes('whatsapp.com') 
        ? convertWhatsAppUrl(item.url) 
        : item.url;
      window.open(url, '_blank');
    } else if (onClick) {
      onClick(item);
    }
  };

  return (
    <button 
      className={`navigation-btn ${className}`}
      onClick={handleClick}
      data-type={item.type}
    >
      {children}
    </button>
  );
};

/**
 * Processa toda a estrutura de configuração do formulário
 */
export const processFormConfig = (formConfig) => {
  if (!formConfig) return formConfig;

  const processStep = (step) => {
    if (!step) return step;

    const processedStep = { ...step };

    // Processa fields se existirem
    if (processedStep.fields && Array.isArray(processedStep.fields)) {
      processedStep.fields = processedStep.fields.map(field => {
        if (field.type === 'navigation' && field.navigationItems) {
          return {
            ...field,
            navigationItems: field.navigationItems.map(navItem => {
              // Processa contactInfo
              if (navItem.contactInfo?.whatsapp) {
                return {
                  ...navItem,
                  contactInfo: {
                    ...navItem.contactInfo,
                    whatsapp: convertWhatsAppUrl(navItem.contactInfo.whatsapp)
                  }
                };
              }
              
              // Processa whatsappUrl direto
              if (navItem.whatsappUrl) {
                return {
                  ...navItem,
                  whatsappUrl: convertWhatsAppUrl(navItem.whatsappUrl)
                };
              }

              return navItem;
            })
          };
        }
        return field;
      });
    }

    return processedStep;
  };

  // Processa todos os steps
  if (formConfig.steps && Array.isArray(formConfig.steps)) {
    return {
      ...formConfig,
      steps: formConfig.steps.map(processStep)
    };
  }

  return formConfig;
};

/**
 * Hook para usar em componentes de fluxo
 */
export const useProcessedFlow = (flow) => {
  return useMemo(() => {
    if (!flow) return flow;

    return {
      ...flow,
      formConfig: processFormConfig(flow.formConfig)
    };
  }, [flow]);
};

/**
 * Componente wrapper para processar automaticamente o fluxo
 */
export const FlowWrapper = ({ flow, children }) => {
  const processedFlow = useProcessedFlow(flow);
  
  // Clona os children e passa o flow processado
  return React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { flow: processedFlow });
    }
    return child;
  });
};

export default {
  useProcessedNavigationItems,
  NavigationButton,
  processFormConfig,
  useProcessedFlow,
  FlowWrapper
};